import { styled, IconButton, Tooltip } from '@mui/material'
import { ReactNode } from 'react'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import { colorPalette } from '../../../../style/colorPalette'
import { CloseButton } from '../commons'

type AddButtonProps = {
  onClick: () => void
  tooltip: string
  icon: ReactNode
}

export const AddButton = ({ onClick, tooltip, icon }: AddButtonProps) => (
  <Tooltip title={tooltip}>
    <AddIconButton onClick={onClick}>{icon}</AddIconButton>
  </Tooltip>
)

const AddIconButton = styled(IconButton)({
  width: '28px',
  height: '28px',
  padding: '4px',
  borderRadius: '6px',
  color: colorPalette.skyBlue[7],
  border: `1px solid ${colorPalette.skyBlue[7]}`,
  '&:hover': {
    backgroundColor: colorPalette.skyBlue[0],
  },
})

export const DialogContent = styled('div')({
  flex: 1,
  padding: '12px 16px 16px',
  overflowY: 'auto',
})

export const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '40px',
  fontSize: '14px',
  color: colorPalette.monotone[7],
})

type SelectMenuHeaderProps = {
  breadcrumb: ReactNode
  title?: string
  actions: ReactNode
  onClose: () => void
}

export const SelectMenuHeader = ({
  breadcrumb,
  title,
  actions,
  onClose,
}: SelectMenuHeaderProps) => (
  <HeaderArea>
    <BreadcrumbBar>
      <BreadcrumbTrail>{breadcrumb}</BreadcrumbTrail>
      <HeaderRight>
        {actions}
        <CloseButton onClick={onClose} />
      </HeaderRight>
    </BreadcrumbBar>
    {title && (
      <TitleBar>
        <PageTitle>{title}</PageTitle>
      </TitleBar>
    )}
  </HeaderArea>
)

const HeaderArea = styled('div')({
  padding: '16px 16px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
})

const BreadcrumbBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
})

const BreadcrumbTrail = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
})

export const BreadcrumbListIcon = styled(FormatListBulletedRoundedIcon)({
  fontSize: '16px',
  color: colorPalette.monotone[5],
  flexShrink: 0,
})

export const BreadcrumbFolderIcon = styled(FolderRoundedIcon)({
  fontSize: '16px',
  color: colorPalette.monotone[5],
  flexShrink: 0,
})

export const BreadcrumbSeparator = styled(NavigateNextRoundedIcon)({
  fontSize: '16px',
  color: colorPalette.monotone[3],
  flexShrink: 0,
})

export const BreadcrumbText = styled('span')({
  fontSize: '13px',
  color: colorPalette.monotone[5],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
})

export const BreadcrumbLink = styled('span')({
  fontSize: '13px',
  color: colorPalette.monotone[5],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': {
    color: colorPalette.skyBlue[7],
    textDecoration: 'underline',
  },
})

export const BreadcrumbCurrent = styled('span')({
  fontSize: '13px',
  fontWeight: 600,
  color: colorPalette.monotone[9],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
})

const TitleBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
})

const PageTitle = styled('h2')({
  margin: 0,
  fontSize: '18px',
  fontWeight: 700,
  color: colorPalette.monotone[10],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const HeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexShrink: 0,
})

export const TableContainer = styled('div')({
  width: '100%',
})

export const TableHeader = styled('div')({
  display: 'flex',
  fontWeight: 600,
  fontSize: '12px',
})

export const TableBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

export const TableRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

export const HeaderCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '8px',
  color: colorPalette.monotone[5],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const DataCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  fontSize: '13px',
  color: colorPalette.monotone[5],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const NameCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
}))

export const NameText = styled('span')({
  fontSize: '13px',
  fontWeight: 600,
  color: colorPalette.monotone[10],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const FolderRowIcon = styled(FolderRoundedIcon)({
  fontSize: '18px',
  color: colorPalette.monotone[5],
  flexShrink: 0,
})

export const ReportRowIcon = styled(InsertDriveFileOutlinedIcon)({
  fontSize: '18px',
  color: colorPalette.monotone[4],
  flexShrink: 0,
})
